import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fieldError, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import type { BrandingTheme } from '../../lib/types';
import { Button, Card, FormField, Input, PageHeader, Spinner } from '../../components/ui';
import { RequirePermission } from '../../lib/auth';

const DEFAULT_THEME: BrandingTheme = {
  clientSlug: '',
  displayName: '',
  logoUrl: '',
  colors: { primary: '#2B3A55', accent: '#7B5EA7', background: '#F5F1E8' },
  fonts: { heading: 'Inter', body: 'Inter' },
  radiusPx: 8,
};

/** Shape matches clients/_template/theme.json exactly (colors.primary/accent/background,
 * fonts.heading/body, radiusPx, logoUrl) per the task brief. */
export function BrandingPage() {
  const queryClient = useQueryClient();
  const [theme, setTheme] = useState<BrandingTheme>(DEFAULT_THEME);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const brandingQuery = useQuery({ queryKey: ['branding'], queryFn: api.branding.get });

  useEffect(() => {
    // The server stores this as an untyped JSON blob (`z.record(z.unknown())` - any tenant that
    // saved branding before `colors`/`fonts` existed, or only ever set `displayName`, has a
    // sparse object on disk) - merge onto DEFAULT_THEME rather than replacing it outright, or a
    // sparse stored value crashes every render on `theme.colors.primary` (real bug, found via a
    // headless click-through of every dashboard tab; reproduced live: tenant A's stored branding
    // is `{ displayName: "Acme Live (QA)" }` with no `colors` key at all).
    if (brandingQuery.data) {
      setTheme({
        ...DEFAULT_THEME,
        ...brandingQuery.data,
        colors: { ...DEFAULT_THEME.colors, ...brandingQuery.data.colors },
        fonts: { ...DEFAULT_THEME.fonts, ...brandingQuery.data.fonts },
      });
    }
  }, [brandingQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => api.branding.update(theme),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['branding'] }),
    onError: (err) => setFormError(toParsedError(err)),
  });

  if (brandingQuery.isLoading) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Branding"
        description="Theme applied to your checkout and buyer-facing pages. Same shape as clients/_template/theme.json."
        actions={
          <RequirePermission permission="branding.manage">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : 'Save branding'}
            </Button>
          </RequirePermission>
        }
      />
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <div className="grid grid-cols-2 gap-6">
        <Card title="Theme">
          <FormField label="Display name" error={fieldError(formError, 'displayName')}>
            <Input
              value={theme.displayName}
              onChange={(e) => setTheme({ ...theme, displayName: e.target.value })}
            />
          </FormField>
          <FormField
            label="Logo"
            hint="Upload placeholder - actual storage (R2) wiring is outside this app's scope"
          >
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
              className="text-sm"
            />
            {logoFile && (
              <p className="text-xs text-slate-500 mt-1">
                Selected: {logoFile.name} (not yet uploaded - no upload endpoint in docs/API.md)
              </p>
            )}
          </FormField>
          <div className="grid grid-cols-3 gap-3">
            <FormField label="Primary color">
              <Input
                type="color"
                value={theme.colors.primary}
                onChange={(e) =>
                  setTheme({ ...theme, colors: { ...theme.colors, primary: e.target.value } })
                }
              />
            </FormField>
            <FormField label="Accent color">
              <Input
                type="color"
                value={theme.colors.accent}
                onChange={(e) =>
                  setTheme({ ...theme, colors: { ...theme.colors, accent: e.target.value } })
                }
              />
            </FormField>
            <FormField label="Background">
              <Input
                type="color"
                value={theme.colors.background}
                onChange={(e) =>
                  setTheme({ ...theme, colors: { ...theme.colors, background: e.target.value } })
                }
              />
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Heading font">
              <Input
                value={theme.fonts.heading}
                onChange={(e) =>
                  setTheme({ ...theme, fonts: { ...theme.fonts, heading: e.target.value } })
                }
              />
            </FormField>
            <FormField label="Body font">
              <Input
                value={theme.fonts.body}
                onChange={(e) =>
                  setTheme({ ...theme, fonts: { ...theme.fonts, body: e.target.value } })
                }
              />
            </FormField>
          </div>
          <FormField label="Corner radius (px)">
            <Input
              type="number"
              min={0}
              max={32}
              value={theme.radiusPx}
              onChange={(e) => setTheme({ ...theme, radiusPx: Number(e.target.value) })}
            />
          </FormField>
        </Card>
        <Card title="Preview">
          <div
            className="p-6 border"
            style={{
              background: theme.colors.background,
              borderRadius: theme.radiusPx,
              fontFamily: theme.fonts.body,
            }}
          >
            <p
              style={{
                color: theme.colors.primary,
                fontFamily: theme.fonts.heading,
                fontWeight: 700,
                fontSize: 18,
              }}
            >
              {theme.displayName || 'Your event name'}
            </p>
            <button
              style={{
                background: theme.colors.primary,
                color: 'white',
                borderRadius: theme.radiusPx,
                padding: '8px 16px',
                marginTop: 12,
              }}
            >
              Buy tickets
            </button>
            <button
              style={{
                background: 'transparent',
                color: theme.colors.accent,
                border: `1px solid ${theme.colors.accent}`,
                borderRadius: theme.radiusPx,
                padding: '8px 16px',
                marginTop: 12,
                marginLeft: 8,
              }}
            >
              Accent action
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
